import apiClient, { getApiErrorMessage, getApiErrorCode } from "./apiClient";
import authService from "./authService";
import { showError, showAuthError } from "@/store/errorStore";

// Códigos de 401 de tenant (claim company_code ausente ou desatualizada)
const TENANT_AUTH_MESSAGES = {
  TENANT005:
    "Sua sessão não tem uma empresa válida. Faça login novamente.",
  TENANT006:
    "Sua empresa foi alterada ou a sessão ficou desatualizada. Faça login novamente.",
};

let installed = false;
let sessionEnding = false;

/**
 * Encerra a sessão uma única vez, mesmo com várias requisições recebendo 401
 */
const endSession = (message) => {
  if (sessionEnding) return;
  sessionEnding = true;
  authService.logout();
  showAuthError(() => {
    sessionEnding = false;
    window.location.href = "/";
  }, message);
};

/**
 * Registra os interceptors do cliente da API (chamar uma vez no AuthProvider).
 * - Injeta o Bearer só nas requisições ao backend (apiClient).
 * - Envia X-Company-Code quando o owner global escolheu uma empresa.
 * - 401: não há refresh token na API; a sessão é encerrada e vai para o login.
 * - Demais erros: modal global com a mensagem do backend (exceto `silent` e
 *   os status em `silentStatuses`).
 */
export const setupAxiosInterceptors = () => {
  if (installed) return;
  installed = true;

  apiClient.interceptors.request.use((config) => {
    const token = authService.getToken();
    if (token && !config.skipAuth) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    const companyCode = config.skipCompanyScope
      ? null
      : authService.getCompanyScopeHeader();
    if (companyCode) {
      config.headers["X-Company-Code"] = companyCode;
    }

    return config;
  });

  apiClient.interceptors.response.use(
    (response) => response,
    (error) => {
      const config = error.config || {};
      const status = error.response?.status;
      const code = getApiErrorCode(error);
      const backendMessage = getApiErrorMessage(error);

      // 401: login inválido é tratado na tela; o resto encerra a sessão
      if (status === 401) {
        if (!config.skipAuth && authService.getToken()) {
          endSession(TENANT_AUTH_MESSAGES[code]);
        }
        return Promise.reject(error);
      }

      if (config.silent || config.silentStatuses?.includes(status)) {
        return Promise.reject(error);
      }

      if (error.response) {
        let errorTitle = "Erro";
        let fallback = "Ocorreu um erro ao processar sua solicitação.";

        switch (status) {
          case 400:
            errorTitle = "Requisição Inválida";
            break;
          case 403:
            errorTitle = "Acesso Negado";
            fallback = "Você não tem permissão para realizar esta ação.";
            break;
          case 404:
            errorTitle = "Não Encontrado";
            fallback = "O registro não foi encontrado nesta empresa.";
            break;
          case 500:
            errorTitle = "Erro no Servidor";
            fallback =
              "Ocorreu um erro interno no servidor. Tente novamente mais tarde.";
            break;
          case 503:
            errorTitle = "Serviço Indisponível";
            fallback =
              "O serviço está temporariamente indisponível. Tente novamente em alguns instantes.";
            break;
          default:
            break;
        }

        // 500 do servlet costuma trazer stack/texto técnico: usa o genérico
        const message =
          status >= 500 ? fallback : backendMessage || fallback;
        showError(message, errorTitle);
      } else if (error.request) {
        showError(
          "Não foi possível conectar ao servidor. Verifique sua conexão com a internet.",
          "Erro de Conexão",
        );
      }

      return Promise.reject(error);
    },
  );
};

export default setupAxiosInterceptors;
